/**
 * Odczyt arkusza miniatur opisanego w WebVTT.
 *
 * Parser jest własny i ma dziesięć linii, bo ten plik generujemy sami: cue to
 * czas, strzałka, czas i adres z fragmentem `#xywh`. Biblioteka do napisów
 * poradziłaby sobie z całym formatem, ale wciągnęlibyśmy ją po to, żeby
 * przeczytać jeden wzorzec.
 *
 * Format jest **standardowy**, a nie nasz własny, i to jest tu istotne: gdyby
 * kiedyś doszedł obcy odtwarzacz albo Chromecast, dostanie plik, który rozumie
 * bez naszego kodu.
 */

export interface SpriteCue {
  fromSec: number;
  toSec: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SpriteSheet {
  imageUrl: string;
  cues: SpriteCue[];
}

/** `hh:mm:ss.mmm` albo `mm:ss.mmm` — WebVTT dopuszcza oba. */
function parseVttTime(raw: string): number | null {
  const parts = raw.trim().split(':');
  if (parts.length < 2 || parts.length > 3) return null;
  const seconds = Number.parseFloat(parts.at(-1) ?? '');
  const minutes = Number.parseInt(parts.at(-2) ?? '', 10);
  const hours = parts.length === 3 ? Number.parseInt(parts[0] ?? '', 10) : 0;
  if (!Number.isFinite(seconds) || !Number.isInteger(minutes) || !Number.isInteger(hours)) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Rozbiera plik VTT na wycinki.
 *
 * `baseUrl` jest potrzebny, bo adresy w pliku są **względne** wobec jego
 * położenia — tak je zapisujemy po stronie serwera, żeby ten sam plik działał
 * pod adresem materiału i po pobraniu na dysk.
 */
export function parseSpriteVtt(vtt: string, baseUrl: string): SpriteSheet | null {
  const cues: SpriteCue[] = [];
  let imageUrl: string | null = null;

  const lines = vtt.split(/\r?\n/);
  for (let i = 0; i < lines.length; i += 1) {
    const arrow = lines[i]?.split('-->');
    if (arrow === undefined || arrow.length !== 2) continue;

    const fromSec = parseVttTime(arrow[0] ?? '');
    const toSec = parseVttTime(arrow[1] ?? '');
    const payload = lines[i + 1]?.trim();
    if (fromSec === null || toSec === null || payload === undefined) continue;

    const [href, fragment] = payload.split('#xywh=');
    if (href === undefined || fragment === undefined) continue;

    const [x, y, w, h] = fragment.split(',').map((n) => Number.parseInt(n, 10));
    if (
      !Number.isInteger(x) ||
      !Number.isInteger(y) ||
      !Number.isInteger(w) ||
      !Number.isInteger(h)
    ) {
      continue;
    }

    // Adres z pierwszego cue wystarcza: arkusz jest jeden na materiał.
    imageUrl ??= new URL(href, baseUrl).toString();
    cues.push({ fromSec, toSec, x: x ?? 0, y: y ?? 0, w: w ?? 0, h: h ?? 0 });
  }

  if (imageUrl === null || cues.length === 0) return null;
  return { imageUrl, cues };
}

/**
 * Wycinek dla danej chwili.
 *
 * Wyszukiwanie **binarne**, nie liniowe: przy przesuwaniu palcem po pasku ta
 * funkcja jest wołana kilkadziesiąt razy na sekundę, a cue jest sto. Poza
 * zakresem oddajemy skrajny kafel, bo pusty podgląd na końcu paska wygląda jak
 * zepsuty arkusz.
 */
export function cueAt(sheet: SpriteSheet, timeSec: number): SpriteCue | null {
  const { cues } = sheet;
  if (cues.length === 0) return null;

  let lo = 0;
  let hi = cues.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const cue = cues[mid];
    if (cue === undefined) break;
    if (timeSec < cue.fromSec) hi = mid - 1;
    else if (timeSec >= cue.toSec) lo = mid + 1;
    else return cue;
  }
  return timeSec < (cues[0]?.fromSec ?? 0) ? (cues[0] ?? null) : (cues.at(-1) ?? null);
}
